import {useCallback, useMemo} from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@mui/material';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {usePlMultiPeriodChainStore} from '@/store/audit/plMultiPeriodChainStore';
import {
    plNameFor,
} from '@/pages/components/fs-report/FsFormula';
import {hasChildren, toNestedTree} from '@/utils/fsDerived';
import {isCode, FS_CODE, findNodeByCodes, normalizeCode} from '@/utils/fsCodes';
import type {FsNodeDraft} from '@/services/auditService/auditAdjustOrchestrate';
import {
    applyPlCotPurCosLinkedAmountsByCode,
    filterHiddenCostOfSalesFlatRows,
    resolveBsInventoriesFromBsTree,
    resolvePlCotCosAmountForParentRollup,
    resolveRawCosForPlCotParentAuditedRollup,
    shouldHidePlCostOfSalesRow,
} from '@/utils/plCostOfSalesVisibility';
import {buildRoundingAdjustmentMaps} from '@/pages/components/fs-report/roundingAdjustHelpers';

interface TaxPLProps {
    annualId: number;
    /** MULTI: prefer periods[].plLine for this YOA */
    yearOfAssessment?: string;
}

// 转换函数（与 ProfitLoss 保持一致）
function convertVO(vo: any): any {
    const children = vo.children && Array.isArray(vo.children)
        ? vo.children.map(convertVO)
        : [];

    return {
        fsId: vo.fsId,
        level: vo.level || 0,
        name: vo.name || '',
        code: vo.code || '',
        entry: (vo.entry || '') as 'Dr' | 'Cr',
        scheduleName: vo.scheduleName || '',
        scheduleCode: vo.scheduleCode || '',
        openingAmount: vo.openingAmount || vo.client || 0,
        adjustment: vo.adjustment || vo.adjustByPapers?.map((paper: any) => ({
            refNo: paper.refNo || '',
            adjustment: paper.adjustment || 0,
            headerId: paper.headerId
        })) || [],
        finalAmount: vo.audited || 0,
        lastYearAudited: vo.lastYearAudited ?? 0,
        adjustByPaper: vo.adjustByPaper || false,
        children: children
    };
}

/** 将 FsNodeDraft 树展平为 code → finalAmount 映射 */
function flattenDraftNodes(nodes: FsNodeDraft[]): Record<string, number> {
    const map: Record<string, number> = {};
    const visit = (list: FsNodeDraft[]) => {
        for (const n of list || []) {
            const code = normalizeCode(n.code);
            if (code) map[code] = n.finalAmount;
            if (Array.isArray(n.children) && n.children.length > 0) {
                visit(n.children);
            }
        }
    };
    visit(nodes);
    return map;
}

function sumNodeAdj(node: any): number {
    const list = Array.isArray(node?.adjustment)
        ? node.adjustment
        : (Array.isArray(node?.adjustByPapers) ? node.adjustByPapers : []);
    return list.reduce((s: number, a: any) => s + (Number(a?.adjustment) || 0), 0);
}

/** MULTI YOA: leaf amounts from a period.plLine tree (already YOA-scoped). */
function flattenAuditedFromPlLine(nodes: any[] | null | undefined): Record<string, number> {
    const map: Record<string, number> = {};
    const visit = (list: any[]) => {
        for (const n of list || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                visit(children);
                continue;
            }
            const code = normalizeCode(n?.code);
            if (!code) continue;
            if (n?.audited != null && n.audited !== '') {
                map[code] = Number(n.audited) || 0;
                continue;
            }
            if (n?.finalAmount != null && n.finalAmount !== '') {
                map[code] = Number(n.finalAmount) || 0;
                continue;
            }
            const client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            map[code] = client + sumNodeAdj(n);
        }
    };
    visit(nodes || []);
    return map;
}

/**
 * MULTI fallback: annual plLine + amountsByYearOfAssessment[yoa].
 * Do not add annual Σ adjustments (not YOA-scoped).
 */
function flattenAuditedFromPlLineForYoa(
    nodes: any[] | null | undefined,
    yoa: string,
): Record<string, number> {
    const map: Record<string, number> = {};
    const yoaKey = String(yoa || '').trim();
    if (!yoaKey) return map;
    const visit = (list: any[]) => {
        for (const n of list || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                visit(children);
                continue;
            }
            const code = normalizeCode(n?.code);
            if (!code) continue;
            const byYoa = n?.amountsByYearOfAssessment as Record<string, number | string> | null | undefined;
            let client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            if (byYoa && typeof byYoa === 'object' && Object.keys(byYoa).length > 0) {
                const raw = byYoa[yoaKey];
                client = (raw !== undefined && raw !== null && raw !== '') ? (Number(raw) || 0) : 0;
            }
            map[code] = client;
        }
    };
    visit(nodes || []);
    return map;
}

export function TaxPL({annualId, yearOfAssessment}: TaxPLProps) {
    const plTreeFromStore = useFsAdjustStore(state => state.adjustRecord?.plLine);
    const periodsFromStore = useFsAdjustStore(state => state.adjustRecord?.periods);
    const yoa = (yearOfAssessment || '').trim();
    const matchedPeriod = useMemo(() => {
        if (!yoa || !Array.isArray(periodsFromStore)) return null;
        return periodsFromStore.find(
            (p: any) => ((p?.yearOfAssessment || '').trim() === yoa),
        ) || null;
    }, [periodsFromStore, yoa]);
    const slicePlLine = useMemo(() => {
        const line = matchedPeriod?.plLine;
        return (Array.isArray(line) && line.length > 0) ? line : null;
    }, [matchedPeriod]);
    const activePlLine = slicePlLine || plTreeFromStore;

    const bsLineFromStore = useFsAdjustStore(state => state.adjustRecord?.bsLine);
    const getComputedDraftNodes = useFsAdjustStore(state => state.getComputedDraftNodes);
    const storeRoundingGroup = useFsAdjustStore(state => state.roundingGroup);
    const auditedBySlice = usePlMultiPeriodChainStore((s) => s.auditedBySlice);

    /**
     * MULTI + YOA：按片过滤父组件 roundingGroup → plDisplayMap。
     * SINGLE：draft finalAmount 已含 rounding，此处置空避免重复叠加。
     */
    const plRoundingAdjustments = useMemo(() => {
        if (!yoa || !storeRoundingGroup) return {} as Record<string, number>;
        return buildRoundingAdjustmentMaps(storeRoundingGroup, {yearOfAssessment: yoa}).plDisplayMap;
    }, [yoa, storeRoundingGroup]);

    const fsTree = useMemo(() => {
        if (!activePlLine) return [] as any[];
        const converted = activePlLine.map(convertVO);
        return toNestedTree(converted);
    }, [activePlLine]);

    const bsTreeNested = useMemo(() => toNestedTree(bsLineFromStore || []), [bsLineFromStore]);

    const hideCostOfSalesRow = useMemo(() => {
        const bsInventories = resolveBsInventoriesFromBsTree(bsTreeNested);
        return shouldHidePlCostOfSalesRow(bsInventories, fsTree);
    }, [bsTreeNested, fsTree]);

    /**
     * code → Final（未叠加 roundingAdjustment）：
     * - MULTI + YOA：该 slice 的 plLine / chain / amountsByYOA（禁止 annual draft Σ）
     * - SINGLE：draftFinalAmount（已含 rounding）
     * Then PUR↔COS linked amounts (same as FsAdjustment Audited / draftFinalAmount).
     */
    const {draftAmountByCode} = useMemo(() => {
        let byCode: Record<string, number> = {};
        if (yoa) {
            const sliceKey = String(matchedPeriod?.key || '').trim();
            const chainMap = sliceKey ? auditedBySlice?.[sliceKey] : null;
            if (chainMap && Object.keys(chainMap).length > 0 && activePlLine) {
                const visit = (list: any[]) => {
                    for (const n of list || []) {
                        const children = Array.isArray(n?.children) ? n.children : [];
                        if (children.length > 0) {
                            visit(children);
                            continue;
                        }
                        const code = normalizeCode(n?.code);
                        const fsId = Number(n?.fsId);
                        if (!code || !fsId) continue;
                        if (Object.prototype.hasOwnProperty.call(chainMap, fsId)) {
                            byCode[code] = Number(chainMap[fsId]) || 0;
                        }
                    }
                };
                visit(activePlLine);
            }
            if (Object.keys(byCode).length === 0) {
                byCode = slicePlLine
                    ? flattenAuditedFromPlLine(slicePlLine)
                    : flattenAuditedFromPlLineForYoa(plTreeFromStore, yoa);
            }
        } else {
            const draft = getComputedDraftNodes();
            byCode = draft ? flattenDraftNodes(draft.plNodes) : {};
        }

        // WHY: opening 取自 FS 树 Client，勿用 Audited final（伴生 COS 会误判录入源）
        const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
        const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
        const cosOpening = Number((cosNode as any)?.openingAmount ?? (cosNode as any)?.amount ?? 0) || 0;
        const purOpening = Number((purNode as any)?.openingAmount ?? (purNode as any)?.amount ?? 0) || 0;
        applyPlCotPurCosLinkedAmountsByCode(byCode, hideCostOfSalesRow, {
            cosOpening,
            purOpening,
        });
        return {draftAmountByCode: byCode};
    }, [
        yoa,
        matchedPeriod?.key,
        auditedBySlice,
        activePlLine,
        slicePlLine,
        plTreeFromStore,
        getComputedDraftNodes,
        hideCostOfSalesRow,
        fsTree,
    ]);

    /** 与 ProfitLoss 一致：叶子取自身 rounding；父节点递归加总子树 + 自身 */
    const sumRoundingForPlNode = useCallback((node: any): number => {
        if (Object.keys(plRoundingAdjustments).length === 0) return 0;
        const code = String(node?.code || '');
        const kids = Array.isArray(node?.children) ? node.children : [];
        if (kids.length === 0) {
            return plRoundingAdjustments[code] ?? 0;
        }
        let sum = 0;
        for (const ch of kids) sum += sumRoundingForPlNode(ch);
        if (plRoundingAdjustments[code] != null) sum += plRoundingAdjustments[code];
        return sum;
    }, [plRoundingAdjustments]);

    const getAuditedValue = useCallback((node: any): number => {
        const code = normalizeCode(node?.code);
        return code ? (draftAmountByCode[code] ?? 0) : 0;
    }, [draftAmountByCode]);

    const getBaseAuditedValue = useCallback((node: any): number => {
        const code = normalizeCode(node?.code);
        return code ? (draftAmountByCode[code] ?? 0) : 0;
    }, [draftAmountByCode]);

    const getRoundedAuditedValue = useCallback((node: any): number => {
        if (isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)) {
            const COS = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
            const PUR = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            const OI = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
            const CI = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
            const ROY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-ROY']);
            const SUB = findNodeByCodes((fsTree || []) as any[], ['PL-COT-SUB']);
            const OCT = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OCT']);
            if (COS || PUR || ROY || SUB || OCT) {
                // Audited(Round)：与 ProfitLoss 同口径（PUR 录入 + 伴生 COS → OI+PUR+CI）
                const cosRounded = COS ? getRoundedAuditedValue(COS) : 0;
                const oiRounded = OI ? getRoundedAuditedValue(OI) : 0;
                const ciRounded = CI ? getRoundedAuditedValue(CI) : 0;
                const purAmt = PUR ? getRoundedAuditedValue(PUR) : 0;
                const cosOpening = COS
                    ? (Number((COS as any)?.openingAmount ?? (COS as any)?.amount ?? 0) || 0)
                    : 0;
                const purOpening = PUR
                    ? (Number((PUR as any)?.openingAmount ?? (PUR as any)?.amount ?? 0) || 0)
                    : 0;
                const primaryCost = resolvePlCotCosAmountForParentRollup({
                    rawCos: resolveRawCosForPlCotParentAuditedRollup({
                        cosOpening,
                        purOpening,
                        cosAuditedRaw: cosRounded,
                        purAudited: purAmt,
                    }),
                    openingInv: oiRounded,
                    purchases: purAmt,
                    closingInv: ciRounded,
                });
                return primaryCost +
                    (ROY ? getRoundedAuditedValue(ROY) : 0) +
                    (SUB ? getRoundedAuditedValue(SUB) : 0) +
                    (OCT ? getRoundedAuditedValue(OCT) : 0);
            }
        }

        // 派生公式含 sumRounding，与 ProfitLoss Audited(Round) 一致
        if (isCode(node, ...FS_CODE.PL.GROSS)) {
            const TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
            const COT = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_TURNOVER);
            return (TURNOVER ? getRoundedAuditedValue(TURNOVER) + sumRoundingForPlNode(TURNOVER) : 0)
                - (COT ? getRoundedAuditedValue(COT) + sumRoundingForPlNode(COT) : 0);
        }

        if (isCode(node, ...FS_CODE.PL.GROSS_AND_OTHER)) {
            const GROSS = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.GROSS);
            const OI = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OTHER_INCOME);
            return (GROSS ? getRoundedAuditedValue(GROSS) + sumRoundingForPlNode(GROSS) : 0)
                + (OI ? getRoundedAuditedValue(OI) + sumRoundingForPlNode(OI) : 0);
        }

        if (isCode(node, ...FS_CODE.PL.BEFORE_TAX)) {
            const GPOI = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.GROSS_AND_OTHER);
            const ADMIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.ADMIN_EXPENSES);
            const FIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.FINANCE_COSTS);
            return (GPOI ? getRoundedAuditedValue(GPOI) + sumRoundingForPlNode(GPOI) : 0)
                - (ADMIN ? getRoundedAuditedValue(ADMIN) + sumRoundingForPlNode(ADMIN) : 0)
                - (FIN ? getRoundedAuditedValue(FIN) + sumRoundingForPlNode(FIN) : 0);
        }

        if (isCode(node, ...FS_CODE.PL.AFTER_TAX)) {
            const PBT = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.BEFORE_TAX);
            const TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TAXATION);
            return (PBT ? getRoundedAuditedValue(PBT) + sumRoundingForPlNode(PBT) : 0)
                - (TAX ? getRoundedAuditedValue(TAX) + sumRoundingForPlNode(TAX) : 0);
        }

        const kids = Array.isArray(node?.children) ? node.children : [];
        if (kids.length === 0) {
            return Math.round(getBaseAuditedValue(node));
        }

        let sum = 0;
        for (const child of kids) {
            sum += getRoundedAuditedValue(child);
        }
        return sum;
    }, [fsTree, getBaseAuditedValue, sumRoundingForPlNode]);

    const getDisplayAuditedValue = useCallback((node: any): number => {
        const isDerived = isCode(
            node,
            ...FS_CODE.PL.COST_OF_TURNOVER,
            ...FS_CODE.PL.GROSS,
            ...FS_CODE.PL.GROSS_AND_OTHER,
            ...FS_CODE.PL.BEFORE_TAX,
            ...FS_CODE.PL.AFTER_TAX,
        );
        if (isDerived || hasChildren(node)) {
            // MULTI：round-then-sum + 子树 rounding 加总；SINGLE：plRounding 为空，行为与修前一致
            return getRoundedAuditedValue(node) + sumRoundingForPlNode(node);
        }
        const base = getAuditedValue(node);
        if (!yoa) return base;
        // 与 ProfitLoss 一致：plDisplayMap 按原始 code 键查找
        const code = String(node?.code || '');
        const adj = code ? (plRoundingAdjustments[code] ?? 0) : 0;
        return Math.round(base) + adj;
    }, [getAuditedValue, getRoundedAuditedValue, plRoundingAdjustments, sumRoundingForPlNode, yoa]);

    const visibleByFsId = useMemo(() => {
        const out: Record<number, boolean> = {};
        const isZero = (n: number) => Math.abs(Number(n) || 0) < 0.01;

        const visit = (node: any): boolean => {
            const kids = Array.isArray(node?.children) ? node.children : [];
            let hasVisibleChild = false;
            for (const ch of kids) {
                if (visit(ch)) hasVisibleChild = true;
            }

            const auditedVal = getDisplayAuditedValue(node);
            let selfVisible = !isZero(auditedVal);
            if (isCode(node, 'PL-COT-COS') && !hideCostOfSalesRow) {
                selfVisible = true;
            }
            if (isCode(node, ...FS_CODE.PL.TAXATION, ...FS_CODE.PL.AFTER_TAX)) {
                selfVisible = false;
                hasVisibleChild = false;
            }

            const visible = selfVisible || hasVisibleChild;
            out[Number(node?.fsId)] = visible;
            return visible;
        };

        for (const root of fsTree) visit(root);
        return out;
    }, [fsTree, getDisplayAuditedValue, hideCostOfSalesRow]);

    const flatten = useCallback((nodes: any[], level = 0): Array<{
        item: any;
        level: number;
        isParent: boolean;
        isSubtotal?: boolean;
        parentItem?: any;
    }> => {
        const out: Array<any> = [];
        for (const n of nodes || []) {
            const isParent = hasChildren(n);
            out.push({item: n, level, isParent});
            if (isParent) {
                const childRows = flatten(n.children, level + 1);
                if ((n as any).level === 2) {
                    childRows.push({
                        item: {...n, fsId: `${n.fsId}_subtotal`, name: ''},
                        level: level + 1,
                        isParent: false,
                        isSubtotal: true,
                        parentItem: n,
                    });
                }
                out.push(...childRows);
            }
        }
        return out;
    }, []);

    const rows = useMemo(() => {
        const flat = flatten(fsTree);
        return hideCostOfSalesRow
            ? filterHiddenCostOfSalesFlatRows(flat, FS_CODE.PL.COST_OF_SALES)
            : flat;
    }, [fsTree, flatten, hideCostOfSalesRow]);

    const formatRounded = (n: number): string => {
        if (Math.abs(n) < 0.01) return '-';
        const abs = Math.abs(Math.round(n));
        const formatted = abs.toLocaleString();
        return n < 0 ? `(${formatted})` : formatted;
    };

    const fallbackName = (it: any): string =>
        (it.name || it.aliasEn || it.nameEn || it.aliasZh || it.nameZh || it.code || '') as string;

    if (!plTreeFromStore || fsTree.length === 0) {
        return <Box sx={{p: 2}}>No PL data available</Box>;
    }

    return (
        <Box sx={{p: 2}}>
            <TableContainer component={Paper}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 700}}/>
                            <TableCell align="right" sx={{width: 180, fontWeight: 700}}>Final</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({item, level, isParent, isSubtotal, parentItem}) => {
                            if (isCode(item, ...FS_CODE.PL.TAXATION, ...FS_CODE.PL.AFTER_TAX)) {
                                return null;
                            }
                            if (!isSubtotal) {
                                const visible = visibleByFsId[Number(item.fsId)];
                                if (visible === false) return null;
                            }

                            let auditedVal = getDisplayAuditedValue(item);
                            const isLevel2Parent = isParent && (item as any).level === 2;
                            if (isSubtotal && parentItem) {
                                auditedVal = getDisplayAuditedValue(parentItem);
                            }
                            if (isSubtotal && Math.abs(auditedVal) < 0.01) return null;

                            const name = isSubtotal ? '' : (plNameFor(item, auditedVal) || fallbackName(item));

                            return (
                                <TableRow key={item.fsId} hover={!isSubtotal} sx={{
                                    '& .MuiTableCell-root': {
                                        paddingTop: '0.5px',
                                        paddingBottom: '0.5px',
                                        ...(isSubtotal && {
                                            borderBottom: '2px solid',
                                            borderBottomColor: 'divider',
                                            fontWeight: 600
                                        })
                                    }
                                }}>
                                    <TableCell>
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <Box sx={{pl: level * 2}}/>
                                            <Typography variant="body2"
                                                        sx={{mr: 1, ...(isSubtotal && {fontWeight: 600})}}>
                                                {name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2"
                                                    sx={{fontSize: 14, textAlign: 'right'}}>
                                            {isLevel2Parent ? '' : formatRounded(
                                                isCode(item, 'PL-COT-CI') ? -Math.abs(auditedVal) : auditedVal
                                            )}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
